import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import EdgeSection, { EdgePlayHeader, getEdgePlay, isSameTeam, isStrongConfidence, isStrongGrade } from './EdgeSection';
const NFLGameCard = ({ game }) => {
    // Check if game is completed and has results
    const isGameFinal = game.result?.final || false;
    const hasUserBet = game.bet_outcome !== undefined;
    const calculateBetGrade = () => {
        // Use the actual bet grade from the new schema if available
        const actualGrade = game.betting_indicators?.bet_ranking?.grade;
        if (actualGrade) {
            return actualGrade;
        }
        // Fallback calculation based on confidence and probability
        const confidence = (game.pick_probability || game.ensemble_probability || 0) * 100;
        const confidenceLevel = getTrueConfidenceLevel();
        if (confidenceLevel === 'HIGH' && confidence >= 70)
            return 'A+';
        if (confidenceLevel === 'HIGH' && confidence >= 65)
            return 'A';
        if (confidenceLevel === 'HIGH')
            return 'A-';
        if (confidenceLevel === 'MEDIUM_PLUS' && confidence >= 60)
            return 'B+';
        if (confidenceLevel === 'MEDIUM_PLUS')
            return 'B';
        if (confidenceLevel === 'MEDIUM' && confidence >= 55)
            return 'B-';
        if (confidenceLevel === 'MEDIUM')
            return 'C+';
        if (confidenceLevel === 'LOW')
            return 'C';
        return 'C-';
    };
    const getBetGradeColor = (grade) => {
        if (!grade)
            return '#6c757d';
        const gradeUpper = grade.toUpperCase();
        if (gradeUpper.startsWith('A'))
            return '#198754';
        if (gradeUpper.startsWith('B'))
            return '#0dcaf0';
        if (gradeUpper.startsWith('C'))
            return '#ffc107';
        if (gradeUpper.startsWith('D'))
            return '#dc3545';
        return '#6c757d';
    };
    const formatGameTime = (gameDate) => {
        try {
            const date = new Date(gameDate);
            if (isNaN(date.getTime())) {
                const parts = gameDate.split('-');
                if (parts.length === 3) {
                    const formattedDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    return formattedDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                }
                return gameDate;
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        }
        catch (e) {
            return gameDate;
        }
    };
    const getBetDisplayText = () => {
        // Show our prediction with the correct spread for the picked team
        const pickedTeam = game.team_to_cover;
        const spread = game.spread_line || 0;
        const spreadText = spread > 0 ? `+${spread}` : `${spread}`;
        return `${pickedTeam} ${spreadText}`;
    };
    const getSpreadOdds = () => {
        // Show the spread odds for our predicted team
        const pickedTeam = game.team_to_cover || game.home_team;
        const homeTeam = game.home_team;
        const isHomePick = pickedTeam === homeTeam;
        // Get the correct odds from betting_analysis
        const bettingAnalysis = game.betting_analysis || game.original_prediction_data?.betting_analysis || {};
        const odds = isHomePick ?
            (bettingAnalysis.spread_home_odds || game.home_spread_odds || -110) :
            (bettingAnalysis.spread_away_odds || game.away_spread_odds || -110);
        return odds > 0 ? `+${odds}` : `${odds}`;
    };
    const getTrueConfidenceLevel = () => {
        return game.confidence_level || 'LOW';
    };
    const getConfidenceColor = () => {
        const trueLevel = getTrueConfidenceLevel();
        switch (trueLevel) {
            case 'HIGH': return 'var(--bs-success)';
            case 'MEDIUM_PLUS': return 'var(--bs-primary)';
            case 'MEDIUM': return 'var(--bs-warning)';
            case 'LOW': return 'var(--bs-secondary)';
            default: return 'var(--bs-secondary)';
        }
    };
    const getPickProbability = () => {
        return Math.round(game.confidence_percentage || 0);
    };
    const getPickDescription = () => {
        return game.team_to_cover;
    };
    const getSideOdds = (side) => {
        const bettingAnalysis = game.betting_analysis || game.original_prediction_data?.betting_analysis || {};
        const odds = side === 'home' ?
            (bettingAnalysis.spread_home_odds || game.home_spread_odds || -110) :
            (bettingAnalysis.spread_away_odds || game.away_spread_odds || -110);
        return odds > 0 ? `+${odds}` : `${odds}`;
    };
    const hasEdge = !!game.edge_context && game.edge_rank !== undefined;
    const play = getEdgePlay(game.edge_context);
    const showConfidence = isStrongConfidence(game.confidence_level) &&
        (!play || isSameTeam(play.team, game.team_to_cover));
    const showGrade = isStrongGrade(game.betting_indicators?.bet_ranking?.grade);
    // Bet outcome indicator
    const getBetOutcomeIndicator = () => {
        if (!hasUserBet || !isGameFinal)
            return null;
        const betWon = game.bet_outcome?.status === 'won';
        const icon = betWon ? '✓' : '✗';
        const color = betWon ? '#22c55e' : '#dc3545';
        return (_jsx("div", { style: {
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: betWon ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                border: `2px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                color: color,
                zIndex: 10
            }, title: betWon ? 'Bet Won!' : 'Bet Lost', children: icon }));
    };
    return (_jsx("div", { className: "card h-100", style: {
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }, onMouseEnter: (e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }, onMouseLeave: (e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        }, children: _jsxs("div", { className: "card-body", style: { padding: '16px', position: 'relative' }, children: [getBetOutcomeIndicator(), isGameFinal && game.result ? (() => {
                    const winner = game.result.winner;
                    const predictedTeam = game.team_to_cover;
                    const predictedWinner = predictedTeam === game.home_team ? 'home' : 'away';
                    const wasCorrect = predictedWinner === winner;
                    const borderColor = wasCorrect ? '#22c55e' : '#dc3545';
                    const bgColor = wasCorrect ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)';
                    const labelColor = wasCorrect ? '#22c55e' : '#dc3545';
                    const label = wasCorrect ? 'FINAL - CORRECT' : 'FINAL - INCORRECT';
                    return (_jsxs("div", { style: {
                            backgroundColor: bgColor,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '6px',
                            padding: '12px',
                            textAlign: 'center',
                            marginBottom: '10px'
                        }, children: [_jsx("div", { style: { fontSize: '11px', color: labelColor, fontWeight: '600', marginBottom: '4px' }, children: label }), _jsxs("div", { style: { fontSize: '20px', fontWeight: '700', color: 'var(--bs-body-color)' }, children: [game.result.away_score, " - ", game.result.home_score] }), _jsxs("div", { style: { fontSize: '12px', color: '#6b7280', marginTop: '4px' }, children: ["Winner: ", game.result.winner === 'home' ? game.home_team : game.away_team] })] }));
                })() : null, !isGameFinal ? (_jsx("div", { className: "d-flex justify-content-between align-items-center mb-3", children: _jsx("div", { className: "team-info", children: _jsxs("h5", { className: "mb-0", style: { fontSize: '18px', fontWeight: '600', color: 'var(--bs-body-color)' }, children: [game.away_team, " @ ", game.home_team] }) }) })) : null, hasEdge ? (_jsx(EdgePlayHeader, { rank: game.edge_rank, play: play, playOdds: play ? getSideOdds(play.side) : undefined, leanText: getBetDisplayText(), leanOdds: getSpreadOdds() })) : (_jsx("div", { className: "betting-line mb-3 bg-light", style: { borderRadius: '6px', padding: '12px' }, children: _jsxs("div", { className: "d-flex justify-content-between align-items-start", children: [_jsx("div", { className: "fw-semibold", style: { fontSize: '16px', color: 'var(--bs-body-color)' }, children: getBetDisplayText() }), _jsx("div", { className: "text-end", children: _jsx("div", { className: "fw-bold", style: { fontSize: '16px', color: '#22c55e' }, children: getSpreadOdds() }) })] }) })), showConfidence && (_jsxs("div", { className: "confidence-section mb-3", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "text-muted", style: { fontSize: '12px' }, children: "Pick Confidence" }), _jsxs("span", { style: { fontSize: '12px', fontWeight: '500', color: 'var(--bs-body-color)' }, children: [getPickProbability(), "% ", getPickDescription()] })] }), _jsx("div", { className: "progress", style: { height: '10px', borderRadius: '5px' }, children: _jsx("div", { className: "progress-bar", style: {
                                    width: `${getPickProbability()}%`,
                                    backgroundColor: getConfidenceColor(),
                                    borderRadius: '5px'
                                } }) })] })), (showConfidence || showGrade) && (_jsx("div", { className: "criteria-status mb-3", children: _jsxs("div", { className: "row g-2", children: [showConfidence && (_jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center p-2 bg-light", style: { borderRadius: '4px' }, children: [_jsx("div", { className: "text-secondary", style: { fontSize: '12px' }, children: "Confidence" }), _jsx("div", { style: { fontSize: '14px', fontWeight: '600', color: getConfidenceColor() }, children: getTrueConfidenceLevel() })] }) })), showGrade && (_jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center p-2 bg-light", style: { borderRadius: '4px' }, children: [_jsx("div", { className: "text-secondary", style: { fontSize: '12px' }, children: "Bet Grade" }), _jsx("div", { style: { fontSize: '14px', fontWeight: '600', color: getBetGradeColor(calculateBetGrade()) }, children: calculateBetGrade() })] }) }))] }) })), _jsx(EdgeSection, { rank: game.edge_rank, context: game.edge_context, homeTeam: game.home_team, awayTeam: game.away_team }), _jsxs(Link, { to: `/dashboard/game/${game.game_id}`, className: "btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center", style: {
                        fontSize: '12px',
                        fontWeight: '500',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        minHeight: '44px'
                    }, children: [_jsx("i", { className: "fas fa-chart-line me-2" }), "View Analysis"] })] }) }));
};
export default NFLGameCard;
